import { Controller } from '@hotwired/stimulus'
import * as d3 from 'd3'
import AxisDrawer from '../charts/cartesian/drawers/axis_drawer'
import LineChartResponse from '../charts/line_chart/line_chart_response'
import LineDrawer from '../charts/line_chart/line_drawer'
import BarChartResponse from '../charts/bar_chart/bar_chart_response'
import BarDrawer from '../charts/bar_chart/bar_drawer'

export default class extends Controller {
  static targets = ['chart']
  static values = { payload: Object, type: String }

  connect () {
    const Response = this.typeValue === 'bar' ? BarChartResponse : LineChartResponse
    this.response = new Response(this.payloadValue)
    this.xAxisValue = this.response.configuration.defaultAxisLabel('x')
    this.yAxisValue = this.response.configuration.defaultAxisLabel('y')
    this.draw()
    this.resizeObserver = new ResizeObserver(() => this.draw())
    this.resizeObserver.observe(this.element)
  }

  disconnect () {
    this.resizeObserver.disconnect()
  }

  draw () {
    const svg = d3.select(this.chartTarget)
    svg.selectAll('*').remove()
    const width = this.element.clientWidth
    const height = this.element.clientHeight
    svg.attr('width', width).attr('height', height)
    const axes = { xAxisLabel: this.xAxisValue, yAxisLabel: this.yAxisValue }
    const series = this.typeValue === 'bar' ? this.response.barsFor(axes) : this.response.linesFor(axes)
    this.layersAboveSize = this.drawLabels(svg, series, width)
    this.chartSize = { width, height: height - this.layersAboveSize }
    const domainSeries = this.typeValue === 'bar' && this.response.options.stacked ? this.stackedDomain(series) : series
    const axis = AxisDrawer.drawAxises({ chartController: this, configuration: this.response.configuration, series: domainSeries, interactive: false })
    const g = svg.append('g').attr('transform', `translate(${axis.margin.left},${axis.margin.top + this.layersAboveSize})`)
    if (this.typeValue === 'bar') {
      BarDrawer.drawBars({ g, xAxis: { scale: axis.xScale }, yAxis: { scale: axis.yScale }, bars: series, options: this.response.options })
    } else {
      LineDrawer.drawLines({ g, xAxis: { scale: axis.xScale, scaleType: axis.xAxisScaleType }, yAxis: { scale: axis.yScale, scaleType: axis.yAxisScaleType },
        lines: series, dataPoints: { show: this.response.options.show_data_points, radius: this.response.options.data_point_radius } })
    }
    if (!series.some(item => item.data.length)) {
      svg.append('text').attr('x', width / 2).attr('y', height / 2).attr('text-anchor', 'middle').attr('fill', '#6b7280').text('No statistics available')
    }
    this.element.dataset.chartReady = 'true'
  }

  drawLabels (svg, series, width) {
    let x = 10
    let y = 18
    series.forEach(item => {
      const label = svg.append('g').attr('class', 'pdf-chart-series-label')
      label.append('rect').attr('width', 16).attr('height', 3).attr('y', -5).attr('fill', item.options.color)
      const text = label.append('text').attr('x', 22).attr('font-size', 12).text(item.options.label)
      const maxWidth = Math.min(360, width - 50)
      const fullLabel = item.options.label || ''
      let name = fullLabel
      while (text.node().getComputedTextLength() > maxWidth && name.length > 1) {
        name = name.slice(0, -1)
        text.text(`${name}…`)
      }
      label.append('title').text(fullLabel)
      const size = text.node().getComputedTextLength() + 48
      if (x + size > width && x > 10) {
        x = 10
        y += 22
      }
      label.attr('transform', `translate(${x},${y})`)
      x += size
    })
    return series.length ? y + 14 : 0
  }

  stackedDomain (series) {
    const totals = new Map()
    series.forEach(item => item.data.forEach(([x, y]) => totals.set(x, (totals.get(x) || 0) + y)))
    return [{ data: Array.from(totals.entries()) }]
  }
}
